import type { FloorImage } from "@sitescene/types";

import type { ShadowFloor } from "../../types/location-map";

import { sortFloorsForList } from "../../utils/location/location-map-utils";

/** One floor plan ready to draw as a Mapbox image source. */
export type FloorMapImage = {
	corners: FloorImage["corners"];
	id: string;
	opacity?: number;
	url: string;
};

const HTTP_URL = /^https?:\/\//i;

/**
 * URL Mapbox can fetch for this floor plan.
 *
 * Prefers a signed `imageUrl` when the caller has one. An `https` key is used
 * directly so a public image can be placed before signed reads exist. A bare
 * S3 key has no display URL yet.
 */
export const floorImageDisplayUrl = (
	image: FloorImage,
	imageUrl?: string,
): null | string => {
	if (imageUrl && HTTP_URL.test(imageUrl)) return imageUrl;
	if (HTTP_URL.test(image.key)) return image.key;
	return null;
};

/** Floors whose saved corners can be drawn on the map right now. */
export const floorMapImages = (
	floors: readonly ShadowFloor[],
): FloorMapImage[] =>
	sortFloorsForList(floors).flatMap((floor) => {
		const image = floor.image;
		if (!image) return [];
		const url = floorImageDisplayUrl(image, floor.imageUrl);
		if (!url) return [];
		return [
			{
				corners: image.corners,
				id: floor.id,
				...(image.opacity !== undefined ? { opacity: image.opacity } : {}),
				url,
			},
		];
	});
